import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RootScope } from '@encomply-core/services/rootscope/rootscope.service';
import { translate } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
    ClearApplicationMiscProperties,
    CommonUtilsService,
    FetchCaptcha,
    fetchCaptchaSuccessState,
    GetApplicationMiscProperties,
    getApplicationMiscPropertiesState,
    GetContextDetailWithKey,
    getContextSuccessState,
    GetSecurityQuestions,
    GetSecurityQuestionsSuccessState,
    Organization,
    ResetPassword,
    User,
    UtilsService,
} from 'taxilla-library';

@Component({
    selector: 'app-resetpassword',
    templateUrl: './resetpassword.component.html',
    styleUrls: ['./resetpassword.component.scss'],
    standalone: false,
})
export class ResetPasswordComponent implements OnInit, OnDestroy {
    organizationId: string;
    userId: string;
    key: string;
    email: string;
    mfaType: string;
    organization: Organization = new Organization({});
    user: User = new User({});
    passwords = {
        password: '',
        confirmPassword: '',
    };
    secretQuestions = [];
    errors: { [property: string]: string[] } = {};
    securityQuestionsStructure = [];
    captchaUrl: string;
    captcha: string;
    passwordPolicyRegex: string;
    passwordPolicyMsg: string;
    passwordPolicyTitle: string;
    settings: { passwordPolicy: string; passwordPolicyMsg: string };
    mmfaQuestions: number;
    unsubscribe = new Subject<void>();
    private isInit = true;

    constructor(
        private _route: ActivatedRoute,
        public R: RootScope,
        private _commonUtils: CommonUtilsService,
        private _libUtils: UtilsService,
        private _store: Store
    ) {}

    // TAF-745: based on the isInit executing fetchOrgAndUserDetails()
    startComponent = async (isInit?: boolean): Promise<void> => {
        if (!this.key) {
            return;
        }
        this.passwords = {
            password: '',
            confirmPassword: '',
        };
        this.organization = new Organization({});
        this.user = new User({});
        this.clearStorage();
        this.errors = {};
        isInit && (await this.getApplicationMiscProps());
    };

    private clearStorage = () => {
        this._commonUtils.clearStorage();
    };

    getApplicationMiscProps = async (): Promise<void> => {
        this._store.dispatch(ClearApplicationMiscProperties());
        this._store.dispatch(GetApplicationMiscProperties());
    };

    public pushQuestions = (count: number): void => {
        count = this.securityQuestionsStructure.length + count;
        for (let i = 0; i < count; i++) {
            if (this.securityQuestionsStructure.length === count) {
                return;
            }
            const id = `${i + 1}`;
            const question = this.securityQuestionsStructure.find(question => question.id === id);
            if (!question) {
                this.securityQuestionsStructure.push({
                    id: id,
                    displayName: `Security Question`,
                    key: '',
                    value: '',
                    errors: {
                        value: [],
                        key: [],
                    },
                });
            }
        }
    };

    fetchOrgAndUserDetails = () => {
        const payload = { key: this.key };
        this._store.dispatch(GetContextDetailWithKey({ payload }));
    };

    setPassword = (): void => {
        this.errors = {};
        const securityQuestionsPayload = this.mfaType === 'SECRET_QUESTION' ? this.buildSecurityQuestionPayload() : {};
        if (this.validatePassword()) {
            this._store.dispatch(
                ResetPassword({
                    payload: {
                        key: this.key,
                        password: this.passwords.password,
                        confirmPassword: this.passwords.confirmPassword,
                        secretQuestionAndAnswersCommand: securityQuestionsPayload,
                        captcha: this.captcha,
                    },
                })
            );
        } else {
            this._libUtils.alertError(translate('Correct the errors to proceed'));
        }
    };

    private validatePassword = (): boolean => {
        let count = 0;
        const passwordMessage = this._libUtils.checkPassword(
            this.passwords.password,
            (this.settings?.passwordPolicy?.length > 0 && this.settings?.passwordPolicy) || this.passwordPolicyRegex,
            (this.settings?.passwordPolicyMsg?.length > 0 && this.settings?.passwordPolicyMsg) || this.passwordPolicyMsg
        );
        if (!this.passwords.password || this.passwords.password.length === 0) {
            this.errors.password = [translate('Enter password')];
            count++;
        } else if (passwordMessage !== true) {
            this.errors.password = [passwordMessage];
            count++;
        }
        if (!this.passwords.confirmPassword || this.passwords.confirmPassword.length === 0) {
            this.errors.confirmPassword = [translate('Enter confirm password')];
            count++;
        } else if (this.passwords.password !== this.passwords.confirmPassword) {
            this.errors.confirmPassword = [translate('Passwords must match')];
            count++;
        }

        if (this.mfaType === 'SECRET_QUESTION' && this.securityQuestionsStructure && this.securityQuestionsStructure.length > 0) {
            this.securityQuestionsStructure.forEach(question => {
                if (!question.key || (question.key && question.key.length === 0)) {
                    question.errors.key = [translate('Please select question')];
                } else {
                    question.errors.key = question.errors.key || [];
                }
                if (!question.value || (question.value && question.value.length === 0)) {
                    question.errors.value = [translate('Please Answer the question')];
                } else {
                    question.errors.value = question.errors.value || [];
                }
            });

            let errorCount = 0;
            this.securityQuestionsStructure.forEach(question => {
                if (
                    question &&
                    question.errors &&
                    ((question.errors.key && question.errors.key.length > 0) || (question.errors.value && question.errors.value.length > 0))
                ) {
                    errorCount = errorCount + 1;
                }
            });
            if (errorCount) {
                count++;
            }
        }
        const isCaptchaValid = this.checkCaptcha();
        return count === 0 && isCaptchaValid;
    };

    checKQuestions = (value, selectedQuestion) => {
        if (value && value.length > 0) {
            let exist = false;
            for (let i = 0; i < this.securityQuestionsStructure.length; i++) {
                const question = this.securityQuestionsStructure[i];
                if (selectedQuestion.id !== question.id && question.key === value) {
                    exist = true;
                }
            }
            for (let j = 0; j < this.securityQuestionsStructure.length; j++) {
                if (this.securityQuestionsStructure[j].id === selectedQuestion.id) {
                    if (exist) {
                        this.securityQuestionsStructure[j].errors.key = [
                            translate('The question has been selected.Please select another question'),
                        ];
                    } else {
                        this.securityQuestionsStructure[j].errors.key = [];
                    }
                }
            }
        }
        this.securityQuestionsStructure.forEach(element => {
            if (element.errors.key.length > 0) {
                let duplicateCount = 0;
                this.securityQuestionsStructure.forEach(question => {
                    if (element.key === question.key) {
                        duplicateCount++;
                    }
                });

                if (duplicateCount < 2) {
                    element.errors.key = [];
                }
            }
        });
    };

    private buildSecurityQuestionPayload = () => {
        const obj = {};
        this.securityQuestionsStructure.forEach(question => {
            obj['secretQuestion' + question.id] = question.key;
            question.value = question.value?.trim();
            obj['secretAnswer' + question.id] = question.value;
        });
        return obj;
    };

    getSecurityQuestions = () => {
        this._store.dispatch(GetSecurityQuestions());
    };

    getCaptcha = () => {
        this.captcha = '';
        const payload = {
            plain: true,
        };
        this._store.dispatch(FetchCaptcha({ payload }));
    };

    checkCaptcha = () => {
        let count = 0;
        this.errors.captcha = [];
        if (!this.captcha || this.captcha.trim() === '') {
            this.errors.captcha = [translate('error_validCaptcha')];
            count++;
        }
        return count === 0;
    };

    intitializeStoreSubscriptions = (): void => {
        this._store
            .select(GetSecurityQuestionsSuccessState)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data !== undefined)
            )
            .subscribe({
                next: (secretQuestions): void => {
                    if (secretQuestions?.length) {
                        secretQuestions.forEach(element => {
                            const obj = {};
                            obj['value'] = element;
                            this.secretQuestions.push(obj);
                        });
                    }
                },
            });
        this._store
            .select(fetchCaptchaSuccessState)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data !== undefined)
            )
            .subscribe({
                next: res => {
                    this.captchaUrl = res.captcha;
                },
            });
        this._store
            .select(getApplicationMiscPropertiesState)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data['x-auth-token'] !== undefined)
            )
            .subscribe({
                next: response => {
                    this.mmfaQuestions = response?.['min-mfa-answers'];
                    if (this.mfaType === 'SECRET_QUESTION') {
                        this.pushQuestions(this.mmfaQuestions);
                    }
                    this.passwordPolicyRegex = response?.['password-policy'];
                    this.passwordPolicyMsg = response?.['password-policy-msg'];
                    this.passwordPolicyTitle =
                        this.passwordPolicyMsg ||
                        translate(
                            'Enter a combination of minimum 8 alpha numerics with atleast 1 upper case,1 lower case letter and a special symbol (like ! @ $ % * & ? #) without spaces.'
                        );
                    this.isInit && this.fetchOrgAndUserDetails();
                    if (this.mfaType === 'SECRET_QUESTION') {
                        this.isInit && this.getSecurityQuestions();
                    }
                    this.isInit && this.getCaptcha();
                    this.isInit = false;
                },
            });
        this._store
            .select(getContextSuccessState)
            .pipe(
                takeUntil(this.unsubscribe),
                filter(data => data !== undefined)
            )
            .subscribe({
                next: response => {
                    this.organization = response.organization;
                    this.user = response.user;
                    this.settings = response?.settings;
                },
            });
    };

    ngOnInit(): void {
        this._route.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
            this.organizationId = params && params.orgId;
            this.userId = params && params.userId;
            this.key = params && params.key;
            this.email = params && params.email;
            this.mfaType = params && params.mfaType;
            this.startComponent(true);
        });
        this.intitializeStoreSubscriptions();
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
